/**
 * useBookingFilters Hook
 *
 * Manages booking filter state including:
 * - Date range selection
 * - Employee/Project/Customer filters
 * - Booking type (hard/soft)
 * - View mode (week/month/year)
 */

import { useAtom, useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useTransition } from "react";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  addWeeks,
  addMonths,
  addYears,
  subWeeks,
  subMonths,
  subYears,
  isSameDay,
} from "date-fns";
import { clearSelectionAtom } from "@/store/selectionStore";
import {
  bookingFiltersAtom,
  bookingViewModeAtom,
  hideWeekendAtom,
  hasActiveFiltersAtom,
  setDateRangeAtom,
  setEmployeesAtom,
  setProjectsAtom,
  setCustomersAtom,
  setBookingTypeAtom,
  clearFiltersAtom,
  resetFiltersAtom,
  bookingTypeAtom,
  selectedCardAtom,
} from "@/store/bookingStore";
import type {
  IServiceEmployee,
  IServiceProject,
  IServiceCustomer,
  BookingViewMode,
} from "@/services/types";
import { BookingType } from "@/services/types";

export function useBookingFilters() {
  // Transition for non-urgent filter updates (keeps UI responsive)
  const [isFilterPending, startTransition] = useTransition();

  // Read filter state
  const filters = useAtomValue(bookingFiltersAtom);
  const [viewMode, setViewMode] = useAtom(bookingViewModeAtom);
  const [hideWeekend, setHideWeekend] = useAtom(hideWeekendAtom);
  const hasActiveFilters = useAtomValue(hasActiveFiltersAtom);
  const persistedBookingType = useAtomValue(bookingTypeAtom);

  // Write-only setters (base versions)
  const setDateRange = useSetAtom(setDateRangeAtom);
  const setEmployeesBase = useSetAtom(setEmployeesAtom);
  const setProjectsBase = useSetAtom(setProjectsAtom);
  const setCustomersBase = useSetAtom(setCustomersAtom);
  const setBookingType = useSetAtom(setBookingTypeAtom);
  const clearFilters = useSetAtom(clearFiltersAtom);
  const resetFilters = useSetAtom(resetFiltersAtom);
  const clearSelection = useSetAtom(clearSelectionAtom);
  const setSelectedCard = useSetAtom(selectedCardAtom);

  // Wrapped setters with transitions for better UI responsiveness
  // These defer the heavy re-renders caused by filter changes
  const setEmployees = useCallback(
    (
      employees:
        | IServiceEmployee[]
        | ((prev: IServiceEmployee[]) => IServiceEmployee[]),
    ) => {
      startTransition(() => {
        setEmployeesBase(employees);
      });
    },
    [setEmployeesBase],
  );

  const setProjects = useCallback(
    (
      projects:
        | IServiceProject[]
        | ((prev: IServiceProject[]) => IServiceProject[]),
    ) => {
      startTransition(() => {
        setProjectsBase(projects);
      });
    },
    [setProjectsBase],
  );

  const setCustomers = useCallback(
    (
      customers:
        | IServiceCustomer[]
        | ((prev: IServiceCustomer[]) => IServiceCustomer[]),
    ) => {
      startTransition(() => {
        setCustomersBase(customers);
      });
    },
    [setCustomersBase],
  );

  // Sync persisted booking type to filters when it changes
  // This ensures the app stays in the selected booking type across page reloads
  // Note: persistedBookingType is loaded asynchronously from localStorage,
  // so we need to sync it once the value is available
  useEffect(() => {
    if (filters.bookingType !== persistedBookingType) {
      setBookingType(persistedBookingType);
    }
  }, [persistedBookingType, filters.bookingType, setBookingType]);

  useEffect(() => {
    const baseDate = filters.dateRange.start;
    let expectedStart = baseDate;
    let expectedEnd = filters.dateRange.end;

    switch (viewMode) {
      case "week":
        expectedStart = startOfWeek(baseDate, { weekStartsOn: 1 });
        expectedEnd = endOfWeek(baseDate, { weekStartsOn: 1 });
        break;
      case "month":
        expectedStart = startOfMonth(baseDate);
        expectedEnd = endOfMonth(baseDate);
        break;
      case "year":
        expectedStart = startOfYear(baseDate);
        expectedEnd = endOfYear(baseDate);
        break;
    }

    const isAligned =
      isSameDay(filters.dateRange.start, expectedStart) &&
      isSameDay(filters.dateRange.end, expectedEnd);

    if (!isAligned) {
      setDateRange({ start: expectedStart, end: expectedEnd });
    }
  }, [filters.dateRange, setDateRange, viewMode]);

  // ============ DATE NAVIGATION ============

  /**
   * Navigate to previous period (week/month/year)
   */
  const goToPrevious = useCallback(() => {
    const { start, end } = filters.dateRange;

    switch (viewMode) {
      case "week":
        setDateRange({
          start: subWeeks(start, 1),
          end: subWeeks(end, 1),
        });
        break;
      case "month":
        setDateRange({
          start: startOfMonth(subMonths(start, 1)),
          end: endOfMonth(subMonths(start, 1)),
        });
        break;
      case "year":
        setDateRange({
          start: subYears(start, 1),
          end: subYears(end, 1),
        });
        break;
    }
  }, [filters.dateRange, viewMode, setDateRange]);

  /**
   * Navigate to next period (week/month/year)
   */
  const goToNext = useCallback(() => {
    const { start, end } = filters.dateRange;

    switch (viewMode) {
      case "week":
        setDateRange({
          start: addWeeks(start, 1),
          end: addWeeks(end, 1),
        });
        break;
      case "month":
        setDateRange({
          start: startOfMonth(addMonths(start, 1)),
          end: endOfMonth(addMonths(start, 1)),
        });
        break;
      case "year":
        setDateRange({
          start: addYears(start, 1),
          end: addYears(end, 1),
        });
        break;
    }
  }, [filters.dateRange, viewMode, setDateRange]);

  /**
   * Navigate to current period (today's week/month/year)
   */
  const goToToday = useCallback(() => {
    const today = new Date();

    switch (viewMode) {
      case "week":
        setDateRange({
          start: startOfWeek(today, { weekStartsOn: 1 }),
          end: endOfWeek(today, { weekStartsOn: 1 }),
        });
        break;
      case "month":
        setDateRange({
          start: startOfMonth(today),
          end: endOfMonth(today),
        });
        break;
      case "year":
        // Year view: calendar year (Jan 1 - Dec 31)
        setDateRange({
          start: startOfYear(today),
          end: endOfYear(today),
        });
        break;
    }
  }, [viewMode, setDateRange]);

  /**
   * Set specific date and adjust range based on view mode
   */
  const setFocusDate = useCallback(
    (date: Date) => {
      switch (viewMode) {
        case "week":
          setDateRange({
            start: startOfWeek(date, { weekStartsOn: 1 }),
            end: endOfWeek(date, { weekStartsOn: 1 }),
          });
          break;
        case "month":
          setDateRange({
            start: startOfMonth(date),
            end: endOfMonth(date),
          });
          break;
        case "year":
          setDateRange({
            start: startOfYear(date),
            end: endOfYear(date),
          });
          break;
      }
    },
    [viewMode, setDateRange],
  );

  // ============ VIEW MODE ============

  /**
   * Change view mode and adjust date range
   */
  const changeViewMode = useCallback(
    (newMode: BookingViewMode, focusDate?: Date) => {
      const targetDate = focusDate ?? filters.dateRange.start;
      setViewMode(newMode);

      // Clear selections when switching views (Bug #1 fix)
      // Prevents confusion from having selected cards that aren't visible in the new view
      clearSelection();
      setSelectedCard(null); // Also close the sidebar

      // Adjust date range for new view mode
      switch (newMode) {
        case "week":
          setDateRange({
            start: startOfWeek(targetDate, { weekStartsOn: 1 }),
            end: endOfWeek(targetDate, { weekStartsOn: 1 }),
          });
          break;
        case "month":
          setDateRange({
            start: startOfMonth(targetDate),
            end: endOfMonth(targetDate),
          });
          break;
        case "year":
          setDateRange({
            start: startOfYear(targetDate),
            end: endOfYear(targetDate),
          });
          break;
      }
    },
    [
      filters.dateRange.start,
      setViewMode,
      setDateRange,
      clearSelection,
      setSelectedCard,
    ],
  );

  // ============ ENTITY FILTERS ============

  /**
   * Add employee to filter
   */
  const addEmployee = useCallback(
    (employee: IServiceEmployee) => {
      setEmployees((prev) => {
        if (prev.some((e) => e.id === employee.id)) {
          return prev;
        }
        return [...prev, employee];
      });
    },
    [setEmployees],
  );

  /**
   * Remove employee from filter
   */
  const removeEmployee = useCallback(
    (employee: IServiceEmployee) => {
      setEmployees((prev) => prev.filter((e) => e.id !== employee.id));
    },
    [setEmployees],
  );

  /**
   * Add project to filter
   */
  const addProject = useCallback(
    (project: IServiceProject) => {
      setProjects((prev) => {
        if (prev.some((p) => p.id === project.id)) {
          return prev;
        }
        return [...prev, project];
      });
    },
    [setProjects],
  );

  /**
   * Remove project from filter
   */
  const removeProject = useCallback(
    (project: IServiceProject) => {
      setProjects((prev) => prev.filter((p) => p.id !== project.id));
    },
    [setProjects],
  );

  /**
   * Add customer to filter
   */
  const addCustomer = useCallback(
    (customer: IServiceCustomer) => {
      setCustomers((prev) => {
        if (prev.some((c) => c.id === customer.id)) {
          return prev;
        }
        return [...prev, customer];
      });
    },
    [setCustomers],
  );

  /**
   * Remove customer from filter
   */
  const removeCustomer = useCallback(
    (customer: IServiceCustomer) => {
      setCustomers((prev) => prev.filter((c) => c.id !== customer.id));
    },
    [setCustomers],
  );

  // ============ DERIVED VALUES ============

  /**
   * Check if we're filtering by employees (vs customers)
   */
  const isEmployeeMode =
    filters.employees.length > 0 || filters.customers.length === 0;

  /**
   * Check if we're filtering by customers
   */
  const isCustomerMode =
    filters.customers.length > 0 && filters.employees.length === 0;

  /**
   * Is hard booking mode
   */
  const isHardBooking = filters.bookingType === BookingType.HARD_BOOKING;

  /**
   * Is soft booking mode
   */
  const isSoftBooking = filters.bookingType === BookingType.SOFT_BOOKING;

  return {
    // State
    filters,
    dateRange: filters.dateRange,
    employees: filters.employees,
    projects: filters.projects,
    customers: filters.customers,
    bookingType: filters.bookingType,
    viewMode,
    hideWeekend,
    hasActiveFilters,
    isFilterPending, // True when filter updates are in progress (deferred)

    // Derived
    isEmployeeMode,
    isCustomerMode,
    isHardBooking,
    isSoftBooking,

    // Date navigation
    goToPrevious,
    goToNext,
    goToToday,
    setFocusDate,
    setDateRange,

    // View mode
    changeViewMode,
    setHideWeekend,

    // Entity filters
    setEmployees,
    addEmployee,
    removeEmployee,
    setProjects,
    addProject,
    removeProject,
    setCustomers,
    addCustomer,
    removeCustomer,

    // Booking type
    setBookingType,

    // Clear/reset
    clearFilters,
    resetFilters,
  };
}
